import React, { useState } from 'react';
import {
  Box, Container, Grid, Paper, Tab, Tabs, Typography, Table, TableHead,
  TableBody, TableRow, TableCell, Chip, Alert, Skeleton, Link, MenuItem,
  Select, FormControl, InputLabel,
} from '@mui/material';
import {
  TrendingUp, People, CardMembership, EmojiEvents, Article,
  PieChart as PieIcon, MonetizationOn, Redeem, Share, CalendarMonth, VpnKey, Publish,
} from '@mui/icons-material';
import ReleaseCalendar from './ReleaseCalendar';
import PatreonSessionSetup from './megaphone/PatreonSessionSetup';
import PatreonStagedReleases from './megaphone/PatreonStagedReleases';
import { useQuery } from '@tanstack/react-query';
import { useUrlIntState } from '../hooks/useUrlState';
import {
  Area, BarChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
  ComposedChart,
} from 'recharts';
import { patreonAPI } from '../services/api';
import SectionTitle from './crt/SectionTitle';
import Panel from './crt/Panel';

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();
const usd = (dollars: number | null | undefined) =>
  `$${(dollars ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const centsToUsd = (cents: number | null | undefined) => usd((cents ?? 0) / 100);

const STATUS_COLOR: Record<string, string> = {
  active_patron: '#00ff41',
  declined_patron: '#ffb000',
  former_patron: '#ff6600',
  unknown: '#5aaa5a',
};

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const PatreonDashboard: React.FC = () => {
  const [active, setActive] = useUrlIntState('pat_tab', 0);
  const [memberStatus, setMemberStatus] = useState<string>('active_patron');

  const { data: overview } = useQuery({
    queryKey: ['patreon-overview'],
    queryFn: patreonAPI.getOverview,
    staleTime: 60000,
  });
  const { data: growth } = useQuery({
    queryKey: ['patreon-growth'],
    queryFn: () => patreonAPI.getGrowth('month', 60),
    staleTime: 60000,
  });
  const { data: status } = useQuery({
    queryKey: ['patreon-status'],
    queryFn: patreonAPI.getStatusBreakdown,
    staleTime: 60000,
  });
  const { data: tiers } = useQuery({
    queryKey: ['patreon-tiers'],
    queryFn: patreonAPI.getTiers,
    staleTime: 60000,
  });
  const { data: top } = useQuery({
    queryKey: ['patreon-top'],
    queryFn: () => patreonAPI.getTopSupporters(50),
    staleTime: 60000,
  });
  const { data: members } = useQuery({
    queryKey: ['patreon-members', memberStatus],
    queryFn: () => patreonAPI.getMembers(200, 0, memberStatus || undefined),
    staleTime: 60000,
  });
  const { data: posts } = useQuery({
    queryKey: ['patreon-posts'],
    queryFn: () => patreonAPI.getPosts(100),
    staleTime: 60000,
  });
  const { data: mrr } = useQuery({
    queryKey: ['patreon-mrr-history'],
    queryFn: () => patreonAPI.getMrrHistory(84),
    staleTime: 60000,
  });
  const { data: benefits } = useQuery({
    queryKey: ['patreon-benefits'],
    queryFn: patreonAPI.getBenefits,
    staleTime: 60000,
  });
  const { data: social } = useQuery({
    queryKey: ['patreon-social-reach'],
    queryFn: patreonAPI.getSocialReach,
    staleTime: 60000,
  });

  if (overview && !overview.configured) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box mb={4}>
          <SectionTitle sub="&gt; Membership tiers, patron growth, pledge analytics_">
            PATREON :: CREATOR CONSOLE
          </SectionTitle>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Configure PATREON_ACCESS_TOKEN and run a sync to populate data.
        </Alert>
      </Container>
    );
  }

  const campaign = overview?.campaign;
  const counts = overview?.counts || {};
  const revenue = overview?.revenue || {};

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <SectionTitle sub="&gt; Membership tiers, patron growth, pledge analytics_">
          PATREON :: CREATOR CONSOLE
        </SectionTitle>
      </Box>

      {campaign && (
        <Panel label="CAMPAIGN" sx={{ mb: 3 }}>
          <Box display="flex" gap={3} alignItems="flex-start">
            {campaign.image_url && (
              <img src={campaign.image_url} alt="" style={{ width: 120, height: 120, objectFit: 'cover', border: '1px solid #1e3a1e' }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" fontWeight={700}>{campaign.creation_name || 'Patreon Campaign'}</Typography>
              <Typography variant="body2" sx={{ color: '#5aaa5a', mt: 0.5 }}>
                <Link href={campaign.url} target="_blank" rel="noopener" sx={{ color: '#00ff41' }}>
                  {campaign.url}
                </Link>
                {' · '}{campaign.is_monthly ? 'monthly' : `per ${campaign.pay_per_name}`}
                {campaign.is_charged_immediately ? ' · charged immediately' : ' · charged 1st of month'}
                {campaign.has_rss ? ` · RSS: ${campaign.rss_feed_title || 'yes'}` : ''}
              </Typography>
              {campaign.summary && (
                <Box
                  sx={{
                    mt: 1.5,
                    color: '#c7e8c7',
                    fontFamily: "'VT323', monospace",
                    fontSize: '16px',
                    '& p': { margin: '0 0 6px 0' },
                    '& strong': { color: '#00ff41' },
                    maxHeight: 120, overflow: 'auto',
                  }}
                  dangerouslySetInnerHTML={{ __html: campaign.summary }}
                />
              )}
            </Box>
          </Box>
        </Panel>
      )}

      {/* KPI row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'ACTIVE PATRONS', value: fmt(counts.active_patrons), sub: `${fmt(counts.patron_count_reported)} reported` },
          { label: 'MONTHLY REVENUE', value: usd(revenue.active_monthly_dollars), sub: `${fmt(counts.active_patrons)} active patrons` },
          { label: 'LIFETIME SUPPORT', value: usd(revenue.campaign_lifetime_support_dollars), sub: 'all-time gross' },
          { label: 'POSTS', value: fmt(overview?.posts?.total), sub: overview?.posts?.last_post_at ? `last: ${new Date(overview.posts.last_post_at).toLocaleDateString()}` : '' },
        ].map((kpi) => (
          <Grid size={{ xs: 6, md: 3 }} key={kpi.label}>
            <Panel label={kpi.label}>
              <Typography variant="h4" fontWeight={700}>{kpi.value}</Typography>
              {kpi.sub && <Typography variant="caption" color="textSecondary">{kpi.sub}</Typography>}
            </Panel>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={active} onChange={(_, v) => setActive(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<MonetizationOn />} label="MRR history" />
          <Tab icon={<TrendingUp />} label="Growth" />
          <Tab icon={<CardMembership />} label="Tiers" />
          <Tab icon={<Redeem />} label="Benefits" />
          <Tab icon={<PieIcon />} label="Status mix" />
          <Tab icon={<EmojiEvents />} label="Top supporters" />
          <Tab icon={<People />} label="Members" />
          <Tab icon={<Article />} label="Posts" />
          <Tab icon={<CalendarMonth />} label="Release calendar" />
          <Tab icon={<Share />} label="Social reach" />
          <Tab icon={<VpnKey />} label="Session" />
          <Tab icon={<Publish />} label="Staged releases" />
        </Tabs>
      </Paper>

      {/* MRR history — reconstructed from v1 pledges ledger */}
      <TabPanel value={active} index={0}>
        {mrr?.has_data ? (
          <>
            <Panel label={`MONTHLY RECURRING REVENUE (${mrr.points.length} months of history)`}>
              <Box sx={{ width: '100%', height: 360 }}>
                <ResponsiveContainer>
                  <ComposedChart data={mrr.points}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                    <XAxis dataKey="month" stroke="#5aaa5a" tickFormatter={(d: string) => d?.substring(0, 7)} />
                    <YAxis yAxisId="left" stroke="#00ff41" tickFormatter={(v: number) => `$${v.toLocaleString()}`} />
                    <YAxis yAxisId="right" orientation="right" stroke="#ff6600" />
                    <Tooltip
                      contentStyle={{ background: '#0a0a0a', border: '1px solid #1e3a1e', color: '#00ff41' }}
                      formatter={(v: any, name: string) => name === 'mrr_dollars' ? usd(Number(v)) : fmt(Number(v))}
                      labelFormatter={(d: string) => d?.substring(0, 7)}
                    />
                    <Legend />
                    <Area yAxisId="left" type="monotone" dataKey="mrr_dollars" name="MRR $" stroke="#00ff41" fill="#00ff41" fillOpacity={0.25} />
                    <Line yAxisId="right" type="monotone" dataKey="active_pledges" name="Active pledges" stroke="#00ffff" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </Panel>
            <Box mt={3}>
              <Panel label="NEW vs CHURNED PLEDGES PER MONTH">
                <Box sx={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={mrr.points}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                      <XAxis dataKey="month" stroke="#5aaa5a" tickFormatter={(d: string) => d?.substring(0, 7)} />
                      <YAxis stroke="#5aaa5a" />
                      <Tooltip
                        contentStyle={{ background: '#0a0a0a', border: '1px solid #1e3a1e', color: '#00ff41' }}
                        labelFormatter={(d: string) => d?.substring(0, 7)}
                      />
                      <Legend />
                      <Bar dataKey="new_pledges" name="New" fill="#00ff41" stackId="flow" />
                      <Bar dataKey="churned_pledges" name="Churned" fill="#ff3344" stackId="flow" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Panel>
            </Box>
          </>
        ) : (
          <Alert severity="info">
            MRR reconstruction requires the Patreon v1 pledges sync. If you haven't yet,
            set PATREON_V1_CLIENT_ID / SECRET / REFRESH_TOKEN / CAMPAIGN_ID in env.
          </Alert>
        )}
      </TabPanel>

      {/* Growth */}
      <TabPanel value={active} index={1}>
        <Panel label="SIGNUPS BY MONTH (from pledge_relationship_start)">
          {growth?.points?.length ? (
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={growth.points} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                <XAxis dataKey="bucket" stroke="#5aaa5a" tickFormatter={(d: string) => d?.substring(0, 7)} />
                <YAxis stroke="#5aaa5a" />
                <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #1e3a1e', color: '#00ff41' }} />
                <Legend />
                <Bar dataKey="signups" fill="#00ff41" name="New signups" />
                <Bar dataKey="still_active" fill="#ffb000" name="Still active today" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Skeleton variant="rectangular" height={360} />}
        </Panel>
      </TabPanel>

      {/* Tiers */}
      <TabPanel value={active} index={2}>
        <Panel label="MEMBERSHIP TIERS">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TITLE</TableCell>
                  <TableCell align="right">PRICE/MO</TableCell>
                  <TableCell align="right">PATRONS</TableCell>
                  <TableCell align="right">IMPLIED MRR</TableCell>
                  <TableCell align="right">POSTS</TableCell>
                  <TableCell>LIMITS</TableCell>
                  <TableCell>PUBLISHED</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(tiers?.tiers || []).map((t: any) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{t.title}</Typography>
                      {t.description && (
                        <Typography variant="caption" color="textSecondary" noWrap sx={{ display: 'block', maxWidth: 400 }}>
                          {t.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{centsToUsd(t.amount_cents)}</TableCell>
                    <TableCell align="right">{fmt(t.patron_count)}</TableCell>
                    <TableCell align="right">{centsToUsd(t.monthly_cents)}</TableCell>
                    <TableCell align="right">{fmt(t.post_count)}</TableCell>
                    <TableCell>
                      {t.user_limit ? `${t.remaining ?? '?'}/${t.user_limit}` : <span style={{ opacity: 0.4 }}>—</span>}
                    </TableCell>
                    <TableCell>
                      <Chip label={t.published ? 'YES' : 'NO'} size="small" color={t.published ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      {/* Benefits */}
      <TabPanel value={active} index={3}>
        <Panel label={`BENEFITS (${fmt(benefits?.benefits?.length)} configured)`}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>BENEFIT</TableCell>
                  <TableCell>TYPE</TableCell>
                  <TableCell>TIERS</TableCell>
                  <TableCell align="right">DELIVERED</TableCell>
                  <TableCell align="right">PENDING</TableCell>
                  <TableCell align="right">DUE TODAY</TableCell>
                  <TableCell>STATUS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(benefits?.benefits || []).map((b: any) => (
                  <TableRow key={b.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{b.title || '(untitled)'}</Typography>
                      {b.description && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', maxWidth: 520, whiteSpace: 'normal' }}>
                          {b.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={b.benefit_type || '—'} sx={{ background: 'transparent', border: '1px solid #1e3a1e', color: '#5aaa5a' }} />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {(b.tiers || []).map((t: any) => (
                          <Chip key={t.id} size="small" label={`${t.title} ($${(t.amount_cents/100).toFixed(2)})`}
                            sx={{ background: 'transparent', border: '1px solid #00ff41', color: '#00ff41', fontSize: '11px' }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{fmt(b.delivered_deliverables_count)}</TableCell>
                    <TableCell align="right">{fmt(b.not_delivered_deliverables_count)}</TableCell>
                    <TableCell align="right">{fmt(b.deliverables_due_today_count)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={b.is_published ? 'published' : (b.is_ended ? 'ended' : 'draft')}
                        sx={{
                          background: 'transparent',
                          color: b.is_published ? '#00ff41' : (b.is_ended ? '#ff6600' : '#5aaa5a'),
                          border: `1px solid ${b.is_published ? '#00ff41' : (b.is_ended ? '#ff6600' : '#1e3a1e')}`,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      {/* Status mix */}
      <TabPanel value={active} index={4}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel label="MEMBER STATUS">
              {status?.buckets?.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={status.buckets}
                      dataKey="count"
                      nameKey="status"
                      cx="50%" cy="50%"
                      outerRadius={110}
                      label={(entry: any) => `${entry.status}: ${entry.count}`}
                    >
                      {status.buckets.map((b: any, i: number) => (
                        <Cell key={i} fill={STATUS_COLOR[b.status] || '#5aaa5a'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #1e3a1e', color: '#00ff41' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Skeleton variant="rectangular" height={320} />}
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel label="REVENUE BY STATUS">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>STATUS</TableCell>
                    <TableCell align="right">COUNT</TableCell>
                    <TableCell align="right">MONTHLY</TableCell>
                    <TableCell align="right">LIFETIME</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(status?.buckets || []).map((b: any) => (
                    <TableRow key={b.status} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          label={b.status}
                          sx={{
                            background: 'transparent',
                            color: STATUS_COLOR[b.status] || '#5aaa5a',
                            border: `1px solid ${STATUS_COLOR[b.status] || '#1e3a1e'}`,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{fmt(b.count)}</TableCell>
                      <TableCell align="right">{usd(b.monthly_dollars)}</TableCell>
                      <TableCell align="right">{usd(b.lifetime_dollars)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Panel>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Top supporters */}
      <TabPanel value={active} index={5}>
        <Panel label="TOP SUPPORTERS BY LIFETIME">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>NAME</TableCell>
                  <TableCell>STATUS</TableCell>
                  <TableCell align="right">NOW/MO</TableCell>
                  <TableCell align="right">LIFETIME</TableCell>
                  <TableCell>SINCE</TableCell>
                  <TableCell>LAST CHARGE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(top?.supporters || []).map((s: any, i: number) => (
                  <TableRow key={s.id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600}>{s.full_name || 'Anonymous'}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        label={s.patron_status || 'unknown'}
                        size="small"
                        sx={{
                          background: 'transparent',
                          color: STATUS_COLOR[s.patron_status] || '#5aaa5a',
                          border: `1px solid ${STATUS_COLOR[s.patron_status] || '#1e3a1e'}`,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{centsToUsd(s.current_monthly_cents)}</TableCell>
                    <TableCell align="right">{usd(s.lifetime_dollars)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{s.pledge_relationship_start ? new Date(s.pledge_relationship_start).toLocaleDateString() : '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{s.last_charge_date ? new Date(s.last_charge_date).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      {/* Members */}
      <TabPanel value={active} index={6}>
        <Panel label="MEMBERS">
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select value={memberStatus} label="Status" onChange={(e) => setMemberStatus(e.target.value as string)}>
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="active_patron">Active patrons</MenuItem>
                <MenuItem value="former_patron">Former patrons</MenuItem>
                <MenuItem value="declined_patron">Declined</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ overflowX: 'auto', maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>NAME</TableCell>
                  <TableCell>STATUS</TableCell>
                  <TableCell align="right">NOW/MO</TableCell>
                  <TableCell align="right">LIFETIME</TableCell>
                  <TableCell>SINCE</TableCell>
                  <TableCell>LAST CHARGE</TableCell>
                  <TableCell>NEXT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(members?.members || []).map((m: any) => (
                  <TableRow key={m.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{m.full_name || 'Anonymous'}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={m.patron_status || 'unknown'}
                        sx={{
                          background: 'transparent',
                          color: STATUS_COLOR[m.patron_status] || '#5aaa5a',
                          border: `1px solid ${STATUS_COLOR[m.patron_status] || '#1e3a1e'}`,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{centsToUsd(m.currently_entitled_amount_cents)}</TableCell>
                    <TableCell align="right">{centsToUsd(m.campaign_lifetime_support_cents ?? m.lifetime_support_cents)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.pledge_relationship_start ? new Date(m.pledge_relationship_start).toLocaleDateString() : '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.last_charge_date ? new Date(m.last_charge_date).toLocaleDateString() : '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.next_charge_date ? new Date(m.next_charge_date).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            Showing {members?.members?.length || 0} of {members?.total || 0}
          </Typography>
        </Panel>
      </TabPanel>

      {/* Posts */}
      <TabPanel value={active} index={7}>
        <Panel label={`POSTS (${fmt(posts?.total)} total)`}>
          <Box sx={{ overflowX: 'auto', maxHeight: 640 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>TITLE</TableCell>
                  <TableCell>PUBLISHED</TableCell>
                  <TableCell>ACCESS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(posts?.posts || []).map((p: any) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      {p.url ? (
                        <Link href={p.url} target="_blank" rel="noopener" sx={{ color: '#00ff41' }}>
                          {p.title || '(untitled)'}
                        </Link>
                      ) : (p.title || '(untitled)')}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {p.published_at ? new Date(p.published_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.is_public ? 'public' : (p.is_paid ? 'paid' : 'patrons')}
                        sx={{
                          background: 'transparent',
                          color: p.is_public ? '#00ffff' : (p.is_paid ? '#ffb000' : '#00ff41'),
                          border: `1px solid ${p.is_public ? '#00ffff' : (p.is_paid ? '#ffb000' : '#00ff41')}`,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      {/* Release calendar */}
      <TabPanel value={active} index={8}>
        <PatreonCalendarTab />
      </TabPanel>

      {/* Social reach — which external platforms patrons are linked to */}
      <TabPanel value={active} index={9}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Panel label="COVERAGE">
              {social ? (
                <Box sx={{ fontFamily: "'VT323', monospace", fontSize: '18px', lineHeight: 1.6 }}>
                  <div>patrons w/ any link: <b>{fmt(social.users_with_any_connection)}</b></div>
                  <div>total patrons:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <b>{fmt(social.total_users)}</b></div>
                  <div>coverage:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <b>{social.total_users ? ((social.users_with_any_connection / social.total_users) * 100).toFixed(1) : '0'}%</b></div>
                </Box>
              ) : <Skeleton variant="rectangular" height={120} />}
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Panel label="PATRONS BY PLATFORM">
              {social?.platforms?.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={social.platforms} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                    <XAxis type="number" stroke="#5aaa5a" />
                    <YAxis dataKey="platform" type="category" width={110} stroke="#5aaa5a" />
                    <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #1e3a1e', color: '#00ff41' }} />
                    <Legend />
                    <Bar dataKey="connected" name="Total connected" fill="#00ffff" />
                    <Bar dataKey="active_connected" name="Active patron" fill="#00ff41" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Skeleton variant="rectangular" height={320} />}
            </Panel>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={active} index={10}>
        <PatreonSessionSetup />
      </TabPanel>
      <TabPanel value={active} index={11}>
        <PatreonStagedReleases />
      </TabPanel>
    </Container>
  );
};

export default PatreonDashboard;

const PATREON_LEGEND = [
  { type: 'public',       label: 'Public',         color: '#00ffff' },
  { type: 'patrons_only', label: 'Patrons only',   color: '#00ff41' },
  { type: 'tiered',       label: 'Tier-gated',     color: '#ffb000' },
  { type: 'paid',         label: 'Paid / PPP',     color: '#ff6600' },
];

const PatreonCalendarTab: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['patreon-calendar', 24],
    queryFn: () => patreonAPI.getCalendar(24),
    staleTime: 5 * 60_000,
  });
  const colorFor = (t: string) =>
    PATREON_LEGEND.find((l) => l.type === t)?.color || '#5aaa5a';
  const events = (data?.events || []).map((e: any) => ({
    date: e.date,
    title: e.title,
    color: colorFor(e.type),
    type: e.type,
    url: e.url || undefined,
  }));
  return (
    <Box sx={{ p: 1 }}>
      <ReleaseCalendar
        heading={`> ${events.length} posts over the last 24 months`}
        events={events}
        legend={PATREON_LEGEND}
      />
    </Box>
  );
};
